import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiMapPin, FiNavigation, FiClock, FiAlertCircle, FiChevronRight } = FiIcons;

const activeRoute = [
  { id: 1, name: 'Home Sector', time: '07:30', status: 'past', type: 'origin' },
  { id: 2, name: 'Station Alpha (Walk)', time: '07:38', status: 'past', type: 'transfer' },
  { id: 3, name: 'Cyberbus 204 (Boarding)', time: '07:42', status: 'active', type: 'transit' },
  { id: 4, name: 'Midtown Hub', time: '07:55', status: 'future', type: 'stop' },
  { id: 5, name: 'Financial District', time: '08:10', status: 'future', type: 'destination' },
];

const savedRoutes = [
  { name: 'Office Standard', duration: '40m', via: 'Neon Line North', status: 'optimal' },
  { name: 'Gym Detour', duration: '55m', via: 'Bus 204 + Walk', status: 'delayed' },
];

export default function Planner() {
  return (
    <motion.div 
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      className="space-y-8"
    >
      <header className="flex justify-between items-end mb-4">
        <div>
          <h2 className="text-3xl font-bold text-neutral-100">Transit Control</h2>
          <p className="text-neutral-400 mt-1 font-mono text-sm">Real-time route telemetry</p>
        </div>
        <button className="bg-emerald-500/10 text-emerald-400 border border-emerald-500/30 px-4 py-2 rounded-lg font-mono text-xs uppercase tracking-wider hover:bg-emerald-500/20 transition-colors flex items-center gap-2">
          <SafeIcon icon={FiNavigation} />
          Recalculate
        </button>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Route Lanes - Futuristic Timeline */}
        <div className="lg:col-span-2 bg-neutral-900/30 border border-neutral-800 rounded-2xl p-6 md:p-8">
          <div className="mb-6 flex justify-between items-center pb-4 border-b border-neutral-800">
            <h3 className="font-mono text-sm uppercase tracking-widest text-neutral-300">Active Trajectory</h3>
            <span className="px-2 py-1 bg-emerald-500/10 text-emerald-400 text-xs rounded border border-emerald-500/20 animate-pulse">
              LIVE
            </span>
          </div>

          <div className="relative pl-6 space-y-8 before:absolute before:inset-0 before:ml-6 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-neutral-700 before:via-emerald-500/50 before:to-neutral-800">
            {activeRoute.map((stop, index) => (
              <div key={stop.id} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group">
                
                {/* Connector Dot */}
                <div className={`absolute left-0 md:left-1/2 w-4 h-4 rounded-full border-4 border-neutral-950 -translate-x-[7px] md:-translate-x-1/2 ${
                  stop.status === 'past' ? 'bg-neutral-600' :
                  stop.status === 'active' ? 'bg-emerald-400 shadow-[0_0_12px_rgba(52,211,153,0.8)]' :
                  'bg-neutral-800'
                }`}>
                  {stop.status === 'active' && (
                    <span className="absolute inset-0 rounded-full animate-ping bg-emerald-400 opacity-50"></span>
                  )}
                </div>

                {/* Content Card */}
                <div className="ml-8 md:ml-0 md:w-[calc(50%-2rem)] w-full">
                  <div className={`p-4 rounded-xl border backdrop-blur-sm transition-all duration-300 ${
                    stop.status === 'active' 
                      ? 'bg-emerald-950/30 border-emerald-500/40 shadow-[0_0_15px_rgba(16,185,129,0.1)]' 
                      : stop.status === 'past'
                        ? 'bg-neutral-950/50 border-neutral-800/50 opacity-60'
                        : 'bg-neutral-900/50 border-neutral-800 hover:border-neutral-700'
                  }`}>
                    <div className="flex justify-between items-start mb-1">
                      <span className={`text-sm font-bold ${stop.status === 'active' ? 'text-emerald-100' : 'text-neutral-300'}`}>
                        {stop.name}
                      </span>
                      <span className="font-mono text-xs text-neutral-500 bg-neutral-950 px-2 py-0.5 rounded border border-neutral-800">
                        {stop.time}
                      </span>
                    </div>
                    <span className="text-xs text-neutral-500 uppercase tracking-wider font-mono">
                      {stop.type}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Side Panel: Saved Routes & Alerts */}
        <div className="space-y-6">
          {/* Alerts */}
          <div className="bg-red-950/20 border border-red-900/30 rounded-2xl p-5">
            <div className="flex items-start gap-3">
              <SafeIcon icon={FiAlertCircle} className="text-red-500 mt-1 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-medium text-red-200 mb-1">Line Disturbance</h4>
                <p className="text-xs text-red-400/80 leading-relaxed">
                  Elevator maintenance at Central Hub. Rerouting walking path suggested. +3 mins expected.
                </p>
              </div>
            </div>
          </div>

          {/* Saved Routes */}
          <div className="bg-neutral-900/30 border border-neutral-800 rounded-2xl p-5">
            <h4 className="font-mono text-sm uppercase tracking-widest text-neutral-400 mb-4 flex items-center gap-2">
              <SafeIcon icon={FiMapPin} />
              Saved Coordinates
            </h4>
            <div className="space-y-3">
              {savedRoutes.map((route, i) => (
                <div key={i} className="group p-3 bg-neutral-950/50 border border-neutral-800/80 rounded-xl hover:border-neutral-700 transition-colors cursor-pointer">
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-sm text-neutral-200">{route.name}</span>
                    <span className={`text-[10px] uppercase font-mono px-1.5 py-0.5 rounded ${
                      route.status === 'optimal' ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20' : 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                    }`}>
                      {route.status}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-xs text-neutral-500 font-mono">
                    <span>{route.via}</span>
                    <span className="flex items-center gap-1"><SafeIcon icon={FiClock} /> {route.duration}</span>
                  </div>
                </div>
              ))}
            </div>
            <button className="w-full mt-4 py-2 border border-dashed border-neutral-700 rounded-xl text-neutral-500 text-xs font-mono uppercase tracking-wider hover:bg-neutral-800/50 hover:text-neutral-300 transition-all flex items-center justify-center gap-2">
              Add New Route
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}